import { useNavigate, useRouterState } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';

/** The slice of the assistant-ui runtime this hook uses (easy to fake in tests). */
export interface ChatThreads {
  threads: {
    mainItem: { getState(): { remoteId?: string }; subscribe(callback: () => void): () => void };
    subscribe(callback: () => void): () => void;
    switchToThread(threadIdOrRemoteId: string): Promise<unknown>;
  };
}

/** `/chat` → null (no conversation in the URL), `/chat/<id>` → id, elsewhere → undefined. */
export function chatIdFromPath(pathname: string): string | null | undefined {
  if (pathname === '/chat' || pathname === '/chat/') return null;
  if (pathname.startsWith('/chat/')) return decodeURIComponent(pathname.slice('/chat/'.length)) || null;
  return undefined;
}

/**
 * Keeps the open conversation in the URL (`/chat/<sessionId>`) so a refresh
 * reopens it.
 *
 *   URL → chat     on load (and when the URL changes), open that conversation;
 *                  an unknown or inaccessible one falls back to a new chat at /chat
 *   chat → URL     the open conversation's session id, once it has one: a new
 *                  conversation stays at /chat until its first message creates
 *                  the session, then the URL becomes /chat/<id>
 *
 * The URL is replaced, not pushed: switching conversations doesn't build up
 * browser history. Only acts while a /chat URL is showing.
 */
export function useChatUrl(runtime: ChatThreads) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const navigate = useNavigate();
  const urlId = chatIdFromPath(pathname);
  // While a conversation from the URL is opening, don't let the (still new)
  // main thread rewrite the URL back to /chat.
  const opening = useRef<string | null>(null);
  const onChat = useRef(urlId !== undefined);
  onChat.current = urlId !== undefined;
  const shownId = useRef(urlId);
  shownId.current = urlId;

  // URL → chat
  useEffect(() => {
    if (!urlId) return;
    if (runtime.threads.mainItem.getState().remoteId === urlId) return;
    opening.current = urlId;
    runtime.threads
      .switchToThread(urlId)
      .catch(() => {
        if (opening.current === urlId) void navigate({ to: '/chat', replace: true });
      })
      .finally(() => {
        if (opening.current === urlId) opening.current = null;
      });
  }, [urlId, runtime, navigate]);

  // chat → URL. Listens to the list AND the main item: the main item's state
  // settles in its own notification after a switch (reading it from the list
  // notification alone gives the previous conversation), so read it after the
  // current notifications finish.
  useEffect(() => {
    let queued = false;
    const sync = () => {
      if (!onChat.current || opening.current) return;
      const remoteId = runtime.threads.mainItem.getState().remoteId;
      if ((remoteId ?? null) === (shownId.current ?? null)) return;
      if (remoteId) void navigate({ to: '/chat/$conversationId', params: { conversationId: remoteId }, replace: true });
      else void navigate({ to: '/chat', replace: true });
    };
    const schedule = () => {
      if (queued) return;
      queued = true;
      queueMicrotask(() => {
        queued = false;
        sync();
      });
    };
    sync();
    const offList = runtime.threads.subscribe(schedule);
    const offMain = runtime.threads.mainItem.subscribe(schedule);
    return () => {
      offList();
      offMain();
    };
  }, [runtime, navigate]);

  // Coming back to the Chat tab (/chat) with a conversation open: put its id back.
  useEffect(() => {
    if (urlId !== null || opening.current) return;
    const remoteId = runtime.threads.mainItem.getState().remoteId;
    if (remoteId) void navigate({ to: '/chat/$conversationId', params: { conversationId: remoteId }, replace: true });
  }, [urlId, runtime, navigate]);
}
